import * as React from 'react';
import Media from 'react-media';

import { Grid, Typography, breakpoints } from '@coursera/cds-core';
import { useVisibilityTracker } from '@coursera/event-pulse/react';

import Markdown from 'bundles/cms/components/Markdown';
import type { EventInfoFieldsFragment } from 'bundles/xddp/__generated__/contentfulTypes';
import Container from 'bundles/xddp/components/shared/Container';
import type { UpcomingEventsProps, UserAgentProps } from 'bundles/xddp/types';

import _t from 'i18n!nls/xddp';

import 'css!bundles/xddp/components/__styles__/homepage/UpcomingEvents';

const MAX_ALLOWED_EVENTS = 4;
const GRID_SIZE = 12;
type GridColumnSizeType = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

const EventInfo = ({ month, day, event }: EventInfoFieldsFragment) => {
  return (
    <div className="rc-EventInfo">
      <div className="date-info">
        <span className="month">{month}</span>
        <strong className="day">{day}</strong>
      </div>
      <div className="event">
        <Markdown
          source={event}
          trackingName="degree_register_event"
          buttonTrackingNameV3="degree_register_event"
          trackingSection="events"
        />
      </div>
    </div>
  );
};

const UpcomingEvents = ({ upcomingEvents, userAgent }: UpcomingEventsProps & UserAgentProps) => {
  const viewRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker('view_page_section', {
    pageSection: {
      sectionName: 'events',
    },
  });

  if (!upcomingEvents || !upcomingEvents.length) {
    return null;
  }

  let events = upcomingEvents;
  let numberOfEvents = upcomingEvents.length;

  if (upcomingEvents.length > MAX_ALLOWED_EVENTS) {
    events = upcomingEvents.slice(MAX_ALLOWED_EVENTS);
    numberOfEvents = MAX_ALLOWED_EVENTS;
  }

  const columnSize = Math.ceil(GRID_SIZE / numberOfEvents) as GridColumnSizeType;

  return (
    <div className="rc-UpcomingEvents rc-CenteredHeaderSection section-highlighted" ref={viewRef}>
      <Container>
        {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
        <Typography className="section-header-center" variant="h2">
          {_t('Events')}
        </Typography>
        <Grid container justifyContent="center">
          {events
            .filter((eventInfo): eventInfo is EventInfoFieldsFragment => eventInfo !== null)
            .map((eventInfo: EventInfoFieldsFragment) => {
              return (
                <Media
                  key={eventInfo.event}
                  query={{ minWidth: breakpoints.values.md }}
                  defaultMatches={userAgent?.isMobileBrowser}
                >
                  {(matches) =>
                    matches ? (
                      <Grid item md={columnSize}>
                        <EventInfo {...eventInfo} />
                      </Grid>
                    ) : (
                      <Grid item sm={10}>
                        <EventInfo {...eventInfo} />
                      </Grid>
                    )
                  }
                </Media>
              );
            })}
        </Grid>
      </Container>
    </div>
  );
};

export default UpcomingEvents;
